import React from "react";

import {
  getCallDetails,
  getCallNotes,
  getPatientAudioForCallDocs,
  getPatientNotesForCallDocs,
  TCommunicationsCall,
  TCommunicationsCallId,
  TCommunicationsCallNote,
  TCommunicationsRecording,
  TDocumentationAudioMetadata,
  TDocumentationPatientNote,
} from "../../../../../services/api-communications";
import type { TSession } from "../../../../../utils/types";
import { isCallTerminal, shouldPollCall } from "../utils/formatters";

type UseCallDetailParams = {
  session: TSession | null | undefined;
  selectedCallId: TCommunicationsCallId | null;
  activeCallId: TCommunicationsCallId | null;
  activeCallClientRequestId: string | null;
  setCalls: React.Dispatch<React.SetStateAction<TCommunicationsCall[]>>;
};

type TLoadCallDetailOptions = {
  silent?: boolean;
  includeExpandedData?: boolean;
};

type UseCallDetailReturn = {
  selectedCall: TCommunicationsCall | null;
  selectedRecordings: TCommunicationsRecording[];
  selectedCallNotes: TCommunicationsCallNote[];
  patientAudio: TDocumentationAudioMetadata[];
  patientNotes: TDocumentationPatientNote[];
  callDetailLoading: boolean;
  callDetailError: string | null;
  setSelectedCall: React.Dispatch<
    React.SetStateAction<TCommunicationsCall | null>
  >;
  setSelectedCallNotes: React.Dispatch<
    React.SetStateAction<TCommunicationsCallNote[]>
  >;
  setPatientAudio: React.Dispatch<
    React.SetStateAction<TDocumentationAudioMetadata[]>
  >;
  setCallDetailError: React.Dispatch<React.SetStateAction<string | null>>;
  loadCallDetail: (
    callId: TCommunicationsCallId,
    options?: TLoadCallDetailOptions,
  ) => Promise<void>;
};

export const useCallDetail = ({
  session,
  selectedCallId,
  activeCallId,
  activeCallClientRequestId,
  setCalls,
}: UseCallDetailParams): UseCallDetailReturn => {
  const [selectedCall, setSelectedCall] =
    React.useState<TCommunicationsCall | null>(null);
  const [selectedRecordings, setSelectedRecordings] = React.useState<
    TCommunicationsRecording[]
  >([]);
  const [selectedCallNotes, setSelectedCallNotes] = React.useState<
    TCommunicationsCallNote[]
  >([]);
  const [patientAudio, setPatientAudio] = React.useState<
    TDocumentationAudioMetadata[]
  >([]);
  const [patientNotes, setPatientNotes] = React.useState<
    TDocumentationPatientNote[]
  >([]);
  const [callDetailLoading, setCallDetailLoading] =
    React.useState<boolean>(false);
  const [callDetailError, setCallDetailError] = React.useState<string | null>(
    null,
  );

  const loadCallDetail = React.useCallback(
    async (callId: TCommunicationsCallId, options?: TLoadCallDetailOptions) => {
      if (!session) {
        return;
      }

      if (!options?.silent) {
        setCallDetailLoading(true);
      }
      setCallDetailError(null);

      try {
        const includeExpandedData =
          options?.includeExpandedData ?? !options?.silent;
        const callAttemptId =
          (activeCallId === callId ? activeCallClientRequestId : null) || null;
        const detailResponse = await getCallDetails(session, callId, {
          callAttemptId,
        });

        setSelectedCall(detailResponse.call);
        setSelectedRecordings(detailResponse.recordings || []);
        setCalls((currentCalls) => {
          if (!currentCalls.length) {
            return currentCalls;
          }

          let didUpdate = false;
          const nextCalls = currentCalls.map((callItem) => {
            if (callItem.call_uuid !== detailResponse.call.call_uuid) {
              return callItem;
            }
            didUpdate = true;
            return detailResponse.call;
          });
          return didUpdate ? nextCalls : currentCalls;
        });

        const shouldFetchPostCallArtifacts =
          includeExpandedData && isCallTerminal(detailResponse.call.status);

        if (shouldFetchPostCallArtifacts) {
          const notesResponse = await getCallNotes(session, callId);
          setSelectedCallNotes(notesResponse.notes || []);
        } else if (includeExpandedData) {
          setSelectedCallNotes([]);
        }

        const nextPatientUuid = detailResponse.call.patient_uuid;

        if (shouldFetchPostCallArtifacts && nextPatientUuid) {
          const [audioResponse, notesByPatientResponse] = await Promise.all([
            getPatientAudioForCallDocs(session, nextPatientUuid),
            getPatientNotesForCallDocs(session, nextPatientUuid),
          ]);

          setPatientAudio(
            (audioResponse.metadata || []).filter(
              (audioItem) => audioItem.type === "phone_call_audio",
            ),
          );
          setPatientNotes(
            (notesByPatientResponse || []).filter(
              (noteItem) =>
                (noteItem.note_type || "").toLowerCase() !== "patient_summary",
            ),
          );
        } else {
          setPatientAudio([]);
          setPatientNotes([]);
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load call detail.";
        setCallDetailError(message);
      } finally {
        if (!options?.silent) {
          setCallDetailLoading(false);
        }
      }
    },
    [activeCallClientRequestId, activeCallId, session, setCalls],
  );

  React.useEffect(() => {
    if (
      selectedCallId === null ||
      !selectedCall ||
      !shouldPollCall(selectedCall)
    ) {
      return;
    }
    if (activeCallId === selectedCallId) {
      // Active browser calls are already polled in CallSessionProvider.
      return;
    }

    const poller = setInterval(() => {
      loadCallDetail(selectedCallId, {
        silent: true,
        includeExpandedData: false,
      }).catch(() => undefined);
    }, 4000);

    return () => clearInterval(poller);
  }, [activeCallId, loadCallDetail, selectedCall, selectedCallId]);

  return {
    selectedCall,
    selectedRecordings,
    selectedCallNotes,
    patientAudio,
    patientNotes,
    callDetailLoading,
    callDetailError,
    setSelectedCall,
    setSelectedCallNotes,
    setPatientAudio,
    setCallDetailError,
    loadCallDetail,
  };
};
